import { useState, useEffect, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import useFetchWithMsal from '../useFetchWithMsal';
import { protectedResources } from '../../authConfig';
import { useUser } from '../../contexts/UserContext';

const permissionsCache = {
  userRole: null,
  lastFetched: null
};

const CACHE_DURATION_MS = 5 * 60 * 1000;

export const useInvoicePermissions = () => {
  const { accounts } = useMsal();
  const { user, loading: userLoading } = useUser();
  const [userRole, setUserRole] = useState(permissionsCache.userRole || '');
  const [isLoading, setIsLoading] = useState(!permissionsCache.userRole);
  const [error, setError] = useState(null);

  const { execute } = useFetchWithMsal({
    scopes: protectedResources.apiScopes.scopes.read,
  });

  const hasPurchaseInvoicePermission = user?.accessRights?.PurchaseInvoicePage;

  useEffect(() => {
    if (userLoading) return;

    if (accounts.length === 0) {
      setIsLoading(false);
      return;
    }

    if (!hasPurchaseInvoicePermission) {
      setUserRole('regular');
      permissionsCache.userRole = 'regular';
      setIsLoading(false);
      return;
    }

    const fetchUserRole = async () => {
      const cacheIsValid = permissionsCache.lastFetched &&
        (Date.now() - permissionsCache.lastFetched < CACHE_DURATION_MS);

      if (permissionsCache.userRole && permissionsCache.userRole !== 'regular' && cacheIsValid) {
        setUserRole(permissionsCache.userRole);
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const response = await execute(
          "GET",
          `${process.env.REACT_APP_BACKEND_BASE_URL}/api/invoice-roles/my-role`
        );

        const roleFromServer = response?.role || response?.data?.role || 'regular';

        setUserRole(roleFromServer);
        permissionsCache.userRole = roleFromServer;
        permissionsCache.lastFetched = Date.now();

      } catch (err) {
        console.error("Error fetching user role:", err);
        setError(err);
        setUserRole('regular');
        permissionsCache.userRole = 'regular';
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserRole();
  }, [accounts.length, execute, hasPurchaseInvoicePermission, userLoading]);

  useEffect(() => {
    const handlePermissionsChanged = () => {
      permissionsCache.userRole = null;
      permissionsCache.lastFetched = null;
      refreshPermissions();
    };

    window.addEventListener('permissions-changed', handlePermissionsChanged);

    return () => {
      window.removeEventListener('permissions-changed', handlePermissionsChanged);
    };
  }, []);

  const refreshPermissions = useCallback(async () => {
    if (accounts.length === 0) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);

      const previousRole = userRole;

      const response = await execute(
        "GET",
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/invoice-roles/my-role`
      );

      const newRole = response?.role || 'regular';

      if (newRole !== previousRole) {
        console.log(`Role updated: ${previousRole} → ${newRole}`);
        setUserRole(newRole);
        permissionsCache.userRole = newRole;
        permissionsCache.lastFetched = Date.now();

        window.dispatchEvent(new CustomEvent('role-updated', {
          detail: { previousRole, newRole }
        }));
      }
    } catch (err) {
      permissionsCache.lastFetched = null;
    } finally {
      setIsLoading(false);
    }
  }, [accounts.length, execute, userRole]);

  const isAdmin = user?.isAdmin || false;
  const canApproveOrReject = () => isAdmin || userRole === 'approver';
  const canUpload = () => isAdmin || userRole === 'regular' || userRole === 'approver';
  const canEdit = () => isAdmin || userRole === 'regular' || userRole === 'approver';
  const canDelete = () => isAdmin || userRole === 'regular' || userRole === 'approver';
  const canView = () => true;

  const hasPermission = (action) => {
    switch (action) {
      case 'APPROVE':
      case 'REJECT':
        return canApproveOrReject();
      case 'UPLOAD':
        return canUpload();
      case 'EDIT':
        return canEdit();
      case 'DELETE':
        return canDelete();
      case 'VIEW':
        return canView();
      default:
        return false;
    }
  };

  return {
    userRole,
    isLoading,
    error,
    canApproveOrReject,
    canUpload,
    canEdit,
    canDelete,
    canView,
    hasPermission,
    refreshPermissions
  };
};